import { useEffect, useRef, useState } from "react";
import { Send, Plus, MessageSquareLock, Paperclip, X, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, downloadDocument } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [newThread, setNewThread] = useState({ subject: "", text: "" });
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const bottomRef = useRef(null);

  const attachFile = async (file) => {
    if (!file) return;
    setAttaching(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name.replace(/\.[^.]+$/, ""));
      fd.append("category", "Other");
      fd.append("description", "Message attachment");
      const res = await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment({ doc_id: res.data.doc_id, name: res.data.name });
    } catch (err) {
      console.error("Attachment upload failed", err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : err.response ? `Attachment upload failed (error ${err.response.status}).` : "No connection — check your internet and try again.");
    } finally {
      setAttaching(false);
    }
  };

  const loadThreads = () => api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {});

  useEffect(() => { loadThreads(); }, []);

  const openThread = async (thread) => {
    setActive(thread);
    try {
      const res = await api.get(`/messages/threads/${thread.thread_id}`);
      setMessages(res.data.messages);
      loadThreads();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      console.error("Thread load failed", err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : err.response ? `Could not load conversation (error ${err.response.status}).` : "No connection — check your internet and try again.");
    }
  };

  const sendReply = async () => {
    if ((!reply.trim() && !attachment) || !active) return;
    setSending(true);
    try {
      await api.post(`/messages/threads/${active.thread_id}`, { text: reply || "(attachment)", attachment_doc_id: attachment?.doc_id || null });
      setReply("");
      setAttachment(null);
      openThread(active);
    } catch (err) {
      console.error("Message send failed", err);
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : err.response
            ? `Message could not be sent (error ${err.response.status}). Your text is still in the box — try again.`
            : "No connection — your message is still in the box. Check your internet and send again."
      );
    } finally {
      setSending(false);
    }
  };

  const createThread = async () => {
    if (!newThread.subject.trim() || !newThread.text.trim()) {
      toast.error("Please add a subject and message.");
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/messages/threads", { ...newThread, attachment_doc_id: attachment?.doc_id || null });
      setNewOpen(false);
      setNewThread({ subject: "", text: "" });
      setAttachment(null);
      await loadThreads();
      openThread(res.data);
    } catch (err) {
      console.error("Thread create failed", err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : err.response ? `Could not start conversation (error ${err.response.status}). Your draft is still here — try again.` : "No connection — your draft is still here. Check your internet and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-messages-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight">Secure Messages</h1>
          <p className="mt-1 text-sm text-mutedink flex items-center gap-1.5"><MessageSquareLock className="h-3.5 w-3.5 text-sage" /> Private between you and your Full Circle team.</p>
        </div>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full" data-testid="new-message-button"><Plus className="mr-2 h-4 w-4" /> New message</Button>
          </DialogTrigger>
          <DialogContent className="bg-surface">
            <DialogHeader><DialogTitle className="font-serif text-navy">Start a conversation</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <Input placeholder="Subject" value={newThread.subject} onChange={(e) => setNewThread({ ...newThread, subject: e.target.value })} data-testid="new-thread-subject" />
              <Textarea rows={5} placeholder="How can we help?" value={newThread.text} onChange={(e) => setNewThread({ ...newThread, text: e.target.value })} data-testid="new-thread-message" />
              <div className="flex items-center gap-3">
                <label className={`cursor-pointer inline-flex items-center gap-1.5 text-xs font-medium text-teal hover:text-teal-dark ${attaching ? "opacity-50 pointer-events-none" : ""}`} data-testid="new-thread-attach">
                  <Paperclip className="h-3.5 w-3.5" /> {attaching ? "Uploading..." : "Attach a file"}
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.heic" onChange={(e) => { attachFile(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
                {attachment && (
                  <span className="flex items-center gap-1.5 text-xs bg-teal-light text-teal rounded-lg px-2.5 py-1.5">
                    {attachment.name}
                    <button onClick={() => setAttachment(null)} aria-label="Remove attachment"><X className="h-3 w-3" /></button>
                  </span>
                )}
              </div>
              <Button onClick={createThread} disabled={sending} className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="new-thread-send">{sending ? "Sending..." : "Send securely"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-2" data-testid="thread-list">
          {threads.length === 0 && (
            <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-6 text-center">No conversations yet. Start one and your team will reply here.</p>
          )}
          {threads.map((t) => (
            <button
              key={t.thread_id}
              onClick={() => openThread(t)}
              data-testid={`thread-${t.thread_id}`}
              className={`w-full text-left rounded-xl border p-4 transition-all ${active?.thread_id === t.thread_id ? "border-teal bg-teal-light/40" : "border-line bg-surface hover:border-teal"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-navy truncate">{t.subject}</p>
                {t.unread > 0 && <span className="h-5 w-5 rounded-full bg-champagne-dark text-white text-[10px] flex items-center justify-center flex-shrink-0">{t.unread}</span>}
              </div>
              <p className="mt-1 text-xs text-mutedink truncate">{t.last_message}</p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 bg-surface border border-line rounded-2xl flex flex-col min-h-[420px]" data-testid="thread-view">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-mutedink p-8 text-center">Choose a conversation to read it here.</div>
          ) : (
            <>
              <div className="border-b border-line px-5 py-4">
                <p className="text-sm font-semibold text-navy">{active.subject}</p>
              </div>
              <div className="flex-1 overflow-auto p-5 space-y-4 max-h-[420px]">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.msg_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${mine ? "bg-teal-dark text-white" : "bg-sand text-ink"}`}>
                        <p className="text-xs font-semibold mb-1 opacity-70">{m.sender_name} {m.sender_role !== "client" && "· CF Full Circle"}</p>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</p>
                        {m.attachment_doc_id && (
                          <button
                            onClick={() => downloadDocument(m.attachment_doc_id, m.attachment_name)}
                            className={`mt-2 flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5 ${mine ? "bg-white/10 text-white hover:bg-white/20" : "bg-surface text-teal hover:bg-teal-light"}`}
                            data-testid={`message-attachment-${m.msg_id}`}
                          >
                            <FileDown className="h-3.5 w-3.5" /> {m.attachment_name || "Attachment"}
                          </button>
                        )}
                        <p className="text-[10px] mt-1.5 opacity-60">{new Date(m.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <div className="border-t border-line p-4">
                {attachment && (
                  <div className="mb-2 flex items-center gap-2 text-xs bg-teal-light text-teal rounded-lg px-3 py-2 w-fit" data-testid="attachment-preview">
                    <Paperclip className="h-3.5 w-3.5" /> {attachment.name}
                    <button onClick={() => setAttachment(null)} aria-label="Remove attachment" data-testid="attachment-remove"><X className="h-3.5 w-3.5" /></button>
                  </div>
                )}
                <div className="flex gap-3">
                  <label className={`cursor-pointer self-end p-2.5 rounded-full border border-line text-mutedink hover:text-teal hover:border-teal transition-colors ${attaching ? "opacity-50 pointer-events-none" : ""}`} data-testid="attach-button" aria-label="Attach a file">
                    <Paperclip className="h-4 w-4" />
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.heic" onChange={(e) => { attachFile(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                  <Textarea
                    rows={1}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                    placeholder="Write a secure message..."
                    className="resize-none"
                    data-testid="reply-input"
                  />
                  <Button onClick={sendReply} disabled={sending || (!reply.trim() && !attachment)} className="bg-teal text-white hover:bg-teal-dark rounded-full px-5 self-end" data-testid="reply-send-button">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
